import { Storage } from './storage';
import type { StorageRead } from './storage';
import { hasSavedBookingHours } from './bookingHours';

// ─────────────────────────────────────────────────────────────────────
// READ THE BOOKING SETTINGS, ONCE, THE SAME WAY EVERYWHERE.
//
// Three documents, each with its own writer right and its own version token:
//   booking_config_v1        the venue setup (hours, durations, courts, the
//                            member default price)               — #1359
//   booking_zones_v1         the price zones                      — #1362
//   booking_guest_prices_v1  the guest default price (#833)       — #1362
//
// The mount loader (`useInitialLoad`) and the slice's «Last inn på nytt»
// (`useBookingSettings.reloadBookingConfig`) both read them through this, so a
// re-read can never mean something different from the first read.
//
// Through `Storage.read`, so a FAILED read is told apart from an absent
// document (#413). `Storage.get` answers `null` for both, and only the absent
// answer records a version — so after a failed `get` the next save went out as
// a PUT with no `ifVersion` and replaced whatever was stored (#1362). Here:
//   ok      → the stored document. A corrupt document is read as empty, which
//             is what the server reads it as; its version WAS recorded, so a
//             save over it is still conditional.
//   absent  → empty. Version 0 is recorded: a save is a create.
//   failed  → `read: false`. Nothing is known, `Storage` recorded no version,
//             and the slice refuses every save of that document until a read
//             answers (#1359, #1362).
// ─────────────────────────────────────────────────────────────────────

export const BOOKING_CONFIG_KEY = 'booking_config_v1';
export const BOOKING_ZONES_KEY = 'booking_zones_v1';
export const BOOKING_GUEST_PRICES_KEY = 'booking_guest_prices_v1';

/**
 * The venue setup has not been read, so no save of it may go out (#1359). A
 * CODE, never a sentence: the screen says it (`booking.view.configUnread.*`).
 */
export const BOOKING_CONFIG_NOT_READ = 'booking_config_not_read';
/** The price zones have not been read, so no save of them may go out (#1362). */
export const BOOKING_ZONES_NOT_READ = 'booking_zones_not_read';
/** The guest default price has not been read, so no save of it may go out (#1362). */
export const BOOKING_GUEST_PRICES_NOT_READ = 'booking_guest_prices_not_read';

/** A save refused before anything was sent, because its document is unread. */
export type BookingNotReadCode =
  | typeof BOOKING_CONFIG_NOT_READ
  | typeof BOOKING_ZONES_NOT_READ
  | typeof BOOKING_GUEST_PRICES_NOT_READ;

const NOT_READ_CODES: ReadonlySet<string> = new Set<BookingNotReadCode>([
  BOOKING_CONFIG_NOT_READ,
  BOOKING_ZONES_NOT_READ,
  BOOKING_GUEST_PRICES_NOT_READ,
]);

/** Whether a save result's code is one of the «not read» refusals above. */
export function isBookingNotRead(code: unknown): code is BookingNotReadCode {
  return typeof code === 'string' && NOT_READ_CODES.has(code);
}

/**
 * One read of one booking document. `read: false` when the read failed;
 * otherwise `parsed` is the stored value, or `undefined` when nothing is
 * stored or what is stored does not parse.
 */
type DocumentRead = { read: true; parsed: unknown } | { read: false };

async function readBookingDocument(key: string, label: string): Promise<DocumentRead> {
  let res: StorageRead;
  try {
    res = await Storage.read(key);
  } catch (e) {
    // `read` answers rather than throws; should a backend throw anyway, that
    // is a failed read, never an empty document.
    console.error(`[Padel24] Feil ved lasting av ${label}:`, e);
    return { read: false };
  }
  if (res.status === 'failed') return { read: false };
  if (res.status === 'absent') return { read: true, parsed: undefined };
  try {
    return { read: true, parsed: JSON.parse(res.value) };
  } catch (e) {
    console.error(`[Padel24] Feil ved lasting av ${label}:`, e);
    return { read: true, parsed: undefined };
  }
}

export interface BookingConfigRead {
  /** The stored document merged over the defaults — what the screens hold. */
  config: Record<string, unknown>;
  /** The document AS STORED (#1357), or null when the read failed. */
  stored: Record<string, unknown> | null;
  /** Whether the stored document carries opening hours, or null when unknown. */
  hoursSaved: boolean | null;
}

export async function readBookingConfig(
  defaults: Record<string, unknown>,
): Promise<BookingConfigRead> {
  const doc = await readBookingDocument(BOOKING_CONFIG_KEY, 'booking-konfig');
  if (!doc.read) return { config: { ...defaults }, stored: null, hoursSaved: null };
  const parsed = doc.parsed;
  const isObject = !!parsed && typeof parsed === 'object';
  return {
    // Merge with defaults so missing fields get sensible values.
    config: isObject ? { ...defaults, ...(parsed as object) } : { ...defaults },
    stored: isObject && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {},
    hoursSaved: hasSavedBookingHours(parsed),
  };
}

export interface BookingZonesRead {
  /**
   * The stored zones — WHAT IS STORED, and nothing else (#1312): no saved zone
   * is an empty list, which the engine reads as «Pris ikke satt». Also empty
   * when the read failed, and then `read` says that it means nothing.
   */
  zones: unknown[];
  /** False when the read failed: no zone save may go out (#1362). */
  read: boolean;
}

export async function readBookingZones(): Promise<BookingZonesRead> {
  const doc = await readBookingDocument(BOOKING_ZONES_KEY, 'pris-soner');
  if (!doc.read) return { zones: [], read: false };
  return { zones: Array.isArray(doc.parsed) ? doc.parsed : [], read: true };
}

export interface BookingGuestPricesRead {
  /** The stored document, or null when nothing (or nothing usable) is stored (#833). */
  guestPrices: Record<string, unknown> | null;
  /** False when the read failed: no guest-price save may go out (#1362). */
  read: boolean;
}

export async function readBookingGuestPrices(): Promise<BookingGuestPricesRead> {
  const doc = await readBookingDocument(BOOKING_GUEST_PRICES_KEY, 'gjestepriser');
  if (!doc.read) return { guestPrices: null, read: false };
  const p = doc.parsed;
  return {
    guestPrices:
      p && typeof p === 'object' && !Array.isArray(p) ? (p as Record<string, unknown>) : null,
    read: true,
  };
}
